import { Component } from '@theme/component';
import { CartAddEvent, CartErrorEvent } from '@theme/events';

/**
 * Create Your Own Bundle.
 *
 * One selection model drives two interfaces:
 *   >= 750px  inline swatches + quantity stepper per card
 *   <  750px  tap a card -> size sheet -> fills a slot in the sticky tray
 *
 * Everything is added in ONE POST to /cart/add.js so the cart recalculates
 * once and the lines cannot race. Pricing belongs to Shopify's automatic
 * discounts; this component only gets the right variants into the cart.
 */

const WORDS = ['NULL', 'ONE', 'TWO', 'THREE', 'FOUR', 'FIVE'];
const MOBILE = '(max-width: 749px)';

class BundleBuilderComponent extends Component {
  #cards = [];
  #abort = new AbortController();
  #sheetCard = null;

  connectedCallback() {
    super.connectedCallback?.();
    const { signal } = this.#abort;

    this.bundleSize = Number(this.dataset.bundleSize) || 2;
    this.mq = window.matchMedia(MOBILE);

    this.tabs = [...this.querySelectorAll('[data-tab]')];
    this.panels = [...this.querySelectorAll('[data-panel]')];
    this.submit = this.querySelector('[data-submit]');
    this.status = this.querySelector('[data-status]');
    this.errorEl = this.querySelector('[data-error]');
    this.offerStrip = this.querySelector('[data-offer-strip]');
    this.slots = [...this.querySelectorAll('[data-slot]')];
    this.barCta = this.querySelector('[data-bar-cta]');
    this.sheet = this.querySelector('[data-sheet]');

    this.#cards = [...this.querySelectorAll('[data-card]')].map((el) => this.#initCard(el));

    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('keydown', this.#onKeydown, { signal });
    this.mq.addEventListener('change', this.#onBreakpoint, { signal });
    this.sheet?.addEventListener('close', () => { this.#sheetCard = null; }, { signal });

    this.#syncOffer();
    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#abort.abort();
  }

  get isMobile() {
    return this.mq.matches;
  }

  #initCard(el) {
    let variants = [];
    try {
      variants = JSON.parse(el.querySelector('[data-variants]')?.textContent || '[]');
    } catch {
      variants = [];
    }

    const groups = [...el.querySelectorAll('[data-controls] [data-option-index]')];

    const card = {
      el,
      variants,
      groups,
      // Preselect options that only have one value, so the customer only
      // chooses what actually varies.
      selected: groups.map((g) => {
        const values = [...g.querySelectorAll('[data-option-value]')];
        return values.length === 1 ? values[0].dataset.optionValue : null;
      }),
      qty: 0,
      note: el.querySelector('[data-note]'),
      qtyValue: el.querySelector('[data-qty-value]'),
      state: el.querySelector('[data-state]'),
    };

    card.selected.forEach((value, i) => {
      if (!value) return;
      groups[i]?.querySelector(`[data-option-value="${CSS.escape(value)}"]`)?.setAttribute('aria-pressed', 'true');
    });

    return card;
  }

  #cardFor(target) {
    const el = target.closest('[data-card]');
    return this.#cards.find((c) => c.el === el);
  }

  #onBreakpoint = () => {
    if (!this.isMobile) this.sheet?.close();
    this.#render();
  };

  #onClick = (event) => {
    const { target } = event;

    const tab = target.closest('[data-tab]');
    if (tab && this.contains(tab)) return this.#selectTab(tab);

    if (target.closest('[data-open-sheet]')) return this.#openSheet(this.#cardFor(target));
    if (target.closest('[data-sheet-close]')) return this.sheet?.close();
    if (target.closest('[data-sheet-add]')) return this.#confirmSheet();

    const swatch = target.closest('[data-option-value]');
    if (swatch && !swatch.disabled) return this.#selectOption(swatch);

    const step = target.closest('[data-step]');
    if (step && !step.disabled) return this.#changeQty(step);

    const slot = target.closest('[data-slot]');
    if (slot && slot.dataset.filled) return this.#clearSlot(slot);

    if (target.closest('[data-submit]') || target.closest('[data-bar-cta]')) return this.#addToCart();
  };

  #onKeydown = (event) => {
    const tab = event.target.closest('[data-tab]');
    if (!tab) return;
    const dir = { ArrowRight: 1, ArrowLeft: -1 }[event.key];
    if (!dir) return;
    event.preventDefault();
    const next = this.tabs[(this.tabs.indexOf(tab) + dir + this.tabs.length) % this.tabs.length];
    this.#selectTab(next);
    next.focus();
  };

  /**
   * Switching tabs clears the bundle: every discount needs BOTH units from its
   * own collection, so a cross-tab bundle would silently lose the discount.
   */
  #selectTab(tab) {
    if (tab.getAttribute('aria-selected') === 'true') return;

    this.tabs.forEach((t) => {
      const on = t === tab;
      t.setAttribute('aria-selected', String(on));
      t.tabIndex = on ? 0 : -1;
    });

    const id = tab.getAttribute('aria-controls');
    this.panels.forEach((p) => { p.hidden = p.id !== id; });

    this.#reset();
    this.#syncOffer();
    this.#render();
  }

  #syncOffer() {
    if (!this.offerStrip) return;
    const active = this.tabs.find((t) => t.getAttribute('aria-selected') === 'true');
    this.offerStrip.textContent = active?.dataset.offerMobile || active?.dataset.offer || '';
  }

  #reset() {
    for (const card of this.#cards) {
      card.qty = 0;
      if (card.note) { card.note.hidden = true; card.note.textContent = ''; }
    }
  }

  #selectOption(button) {
    const card = this.#cardFor(button) || this.#sheetCard;
    if (!card) return;

    // The sheet renders clones, so match the group by its index attribute.
    const index = Number(button.closest('[data-option-index]').dataset.optionIndex);
    const value = button.dataset.optionValue;

    // Clicking the chosen value again clears it, so a size can be undone
    // without having to pick a different one.
    card.selected[index] = card.selected[index] === value ? null : value;

    // Clearing an option leaves no resolvable variant, so drop the quantity.
    if (card.qty > 0 && !this.#variantFor(card)) card.qty = 0;

    this.#render();
    if (this.#sheetCard === card) this.#renderSheet();
  }

  #changeQty(button) {
    const card = this.#cardFor(button);
    if (!card) return;
    const step = Number(button.dataset.step);

    if (step > 0) {
      if (!this.#variantFor(card)) return this.#flag(card);
      if (this.#total() >= this.bundleSize) return;
    }

    card.qty = Math.max(0, Math.min(this.bundleSize, card.qty + step));
    if (card.note) card.note.hidden = true;
    this.#render();
  }

  #flag(card) {
    if (!card.note) return;
    const missing = card.groups
      .map((g, i) => (card.selected[i] ? null : g.querySelector('.bundle-card__label')?.textContent.trim()))
      .filter(Boolean);
    card.note.textContent = missing.length ? `${missing.join(' and ')} first` : 'That combination is sold out';
    card.note.hidden = false;
  }

  #variantFor(card) {
    if (card.selected.some((v) => v == null)) return null;
    return card.variants.find(
      (v) => v.available && card.selected.every((value, i) => v.options[i] === value)
    ) || null;
  }

  #activeCards() {
    const panel = this.panels.find((p) => !p.hidden);
    return this.#cards.filter((c) => panel?.contains(c.el));
  }

  #total() {
    return this.#activeCards().reduce((sum, c) => sum + c.qty, 0);
  }

  /* ---------------------------------------------------------------- sheet */

  #openSheet(card) {
    if (!card || !this.sheet) return;
    this.#sheetCard = card;

    this.querySelector('[data-sheet-thumb]').src = card.el.dataset.thumb || '';
    this.querySelector('[data-sheet-title]').textContent = card.el.dataset.title || '';
    this.querySelector('[data-sheet-price]').textContent = card.el.dataset.price || '';

    // Clone the card's option groups so the sheet and the inline controls stay
    // one source of truth rather than two parallel markup trees.
    const host = this.querySelector('[data-sheet-options]');
    host.replaceChildren(...card.groups.map((g) => g.cloneNode(true)));

    this.#renderSheet();
    this.sheet.showModal();
  }

  #renderSheet() {
    const card = this.#sheetCard;
    if (!card) return;

    const host = this.querySelector('[data-sheet-options]');
    [...host.querySelectorAll('[data-option-index]')].forEach((group) => {
      const index = Number(group.dataset.optionIndex);
      group.querySelectorAll('[data-option-value]').forEach((btn) => {
        const value = btn.dataset.optionValue;
        btn.setAttribute('aria-pressed', String(card.selected[index] === value));
        btn.disabled = !this.#optionPossible(card, index, value);
      });
    });

    const variant = this.#variantFor(card);
    const full = this.#total() >= this.bundleSize;
    const cta = this.querySelector('[data-sheet-add]');
    const note = this.querySelector('[data-sheet-note]');

    const soldOut = [...host.querySelectorAll('[data-option-value][disabled]')]
      .map((b) => b.dataset.optionValue);
    if (note) {
      note.textContent = soldOut.length ? `${soldOut.join(', ')} sold out` : '';
      note.hidden = soldOut.length === 0;
    }

    // Already in the bundle and room left -> offer another unit.
    const another = card.qty > 0 && !full;
    cta.disabled = !variant || (full && card.qty === 0);
    cta.textContent = !variant
      ? 'Select a size'
      : full && card.qty === 0
        ? 'Bundle is full'
        : another
          ? 'Add another'
          : card.qty > 0
            ? 'Update size'
            : 'Add to bundle';
  }

  #optionPossible(card, index, value) {
    const trial = [...card.selected];
    trial[index] = value;
    return card.variants.some(
      (v) => v.available && trial.every((val, i) => val == null || v.options[i] === val)
    );
  }

  #confirmSheet() {
    const card = this.#sheetCard;
    if (!card) return;
    if (!this.#variantFor(card)) return;

    const full = this.#total() >= this.bundleSize;
    if (card.qty === 0 && full) return;
    if (!full) card.qty = Math.min(this.bundleSize, card.qty + 1);

    this.sheet?.close();
    this.#render();
  }

  #clearSlot(slot) {
    const id = slot.dataset.cardId;
    const card = this.#cards.find((c) => c.el.dataset.productId === id);
    if (!card) return;
    card.qty = Math.max(0, card.qty - 1);
    this.#render();
  }

  /* --------------------------------------------------------------- render */

  #render() {
    const total = this.#total();
    const full = total >= this.bundleSize;
    const remaining = this.bundleSize - total;

    for (const card of this.#cards) {
      card.groups.forEach((group, index) => {
        group.querySelectorAll('[data-option-value]').forEach((btn) => {
          const value = btn.dataset.optionValue;
          // Selected state for the inline swatches. Without this the desktop
          // and tablet controls never show which size is chosen.
          btn.setAttribute('aria-pressed', String(card.selected[index] === value));
          // Grey out values that cannot resolve to an available variant.
          btn.disabled = !this.#optionPossible(card, index, value);
        });
      });

      if (card.qtyValue) card.qtyValue.textContent = WORDS[card.qty] ?? String(card.qty);

      const minus = card.el.querySelector('[data-step="-1"]');
      const plus = card.el.querySelector('[data-step="1"]');
      if (minus) minus.disabled = card.qty === 0;
      if (plus) plus.disabled = full;

      // Mobile card state
      if (card.qty > 0) {
        card.el.setAttribute('data-selected', '');
        const size = card.selected.filter(Boolean).join(' / ');
        if (card.state) {
          card.state.textContent = card.qty > 1
            ? `${size} · ${card.qty} in bundle`
            : `${size} · Tap to change`;
        }
      } else {
        card.el.removeAttribute('data-selected');
        if (card.state) card.state.textContent = 'Tap to select size';
      }
    }

    // Slot tray
    const filled = [];
    for (const card of this.#activeCards()) {
      for (let i = 0; i < card.qty; i += 1) filled.push(card);
    }
    this.slots.forEach((slot, i) => {
      const card = filled[i];
      if (card) {
        // 'true', not '' — an empty dataset value is falsy in JS, so the
        // slot-click handler would never fire even though CSS [data-filled] matches.
        slot.dataset.filled = 'true';
        slot.dataset.cardId = card.el.dataset.productId;
        slot.style.backgroundImage = `url("${card.el.dataset.thumb}")`;
        slot.setAttribute('aria-label', `Remove ${card.el.dataset.title}`);
      } else {
        delete slot.dataset.filled;
        delete slot.dataset.cardId;
        slot.style.backgroundImage = '';
        slot.removeAttribute('aria-label');
      }
    });

    const label = full ? this.#ctaLabel() : `Select ${remaining} more`;
    if (this.status) this.status.textContent = full ? `Bundle ready — ${total} selected` : `Select ${remaining} more`;
    if (this.submit) this.submit.disabled = !full;
    if (this.barCta) {
      this.barCta.disabled = !full;
      this.barCta.textContent = label;
    }

    if (this.errorEl && full) this.errorEl.hidden = true;
  }

  #ctaLabel() {
    const active = this.tabs.find((t) => t.getAttribute('aria-selected') === 'true');
    const offer = active?.dataset.offer;
    const base = this.submit?.textContent.trim() || 'Add to cart';
    return offer ? `${base} · ${offer.replace(/^BUY\s*\d+\s*@?/i, '₹')}` : base;
  }

  /* ----------------------------------------------------------------- cart */

  async #addToCart() {
    const items = [];
    for (const card of this.#activeCards()) {
      if (card.qty <= 0) continue;
      const variant = this.#variantFor(card);
      if (!variant) return this.#flag(card);
      items.push({ id: variant.id, quantity: card.qty });
    }
    if (!items.length) return;

    const buttons = [this.submit, this.barCta].filter(Boolean);
    buttons.forEach((b) => { b.disabled = true; b.setAttribute('aria-busy', 'true'); });
    if (this.errorEl) this.errorEl.hidden = true;

    try {
      // Single request: Shopify recalculates the cart and its automatic
      // discounts exactly once.
      const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.description || data?.message || 'Could not add to cart');

      this.dispatchEvent(new CartAddEvent({}, this.id || 'bundle-builder', { source: 'bundle-builder' }));
      this.#reset();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Could not add to cart';
      if (this.errorEl) { this.errorEl.textContent = message; this.errorEl.hidden = false; }
      this.dispatchEvent(new CartErrorEvent(this.id || 'bundle-builder', message));
    } finally {
      buttons.forEach((b) => b.removeAttribute('aria-busy'));
      this.#render();
    }
  }
}

if (!customElements.get('bundle-builder-component')) {
  customElements.define('bundle-builder-component', BundleBuilderComponent);
}
